import { useQuery } from '@tanstack/react-query'
import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router'

import { EMERGENCY_TYPES } from '../../api/emergency'
import { useAuth } from '../AuthContext'
import { ApiError } from '../api/client'
import {
  EMERGENCY_LEADS_KEY,
  fetchEmergencyLeads,
  type AdminEmergencyLead,
} from '../api/emergencyRequests'
import { COORDINATION_PATH } from '../nav'
import { SEARCHABLE } from '../searchable'
import { useServerSearch } from '../TableFilterContext'
import { EmptyState } from '../components/EmptyState'
import { ListDisclosure } from '../components/ListDisclosure'
import { FilterTabs } from '../components/FilterTabs'
import { RecordCard, type Fact } from '../components/RecordCard'
import { compactAge } from '../api/dashboard'

const STATUS_FILTERS = [
  { key: null, label: 'All' },
  { key: 'new', label: 'New' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'responded', label: 'Responded' },
  { key: 'closed', label: 'Closed' },
] as const

// M17-T14: the amber pill takes text-ink-1, not text-bed-warn. --bed-warn on
// --bed-warn-bg measures 3.76:1, below AA for pill text at this size. The amber
// stays as the status signal on the background; only the label goes dark. There
// is no dark step on the warning ramp to reach for — audit G35, Figma publishes
// no consumable warning ramp — so when the designer supplies one it lands here.

const TYPE_LABELS: Record<string, string> = Object.fromEntries(
  EMERGENCY_TYPES.map((t) => [t.slug, t.label]),
)

function typeLabel(slug: string): string {
  return TYPE_LABELS[slug] ?? slug.replace(/_/g, ' ')
}

const DISPOSITION_LABELS: Record<string, string> = {
  called_er: 'Called the ER',
  private_transport: 'Going by private transport',
  // Before 2026-10-21 this outcome came from an "I also need an ambulance"
  // card; M34-T02 replaced it with a plain "Call the AMTC team". Rows before
  // that date were ambulance requests; rows after are general calls for help.
  call_amtc: 'Called AMTC',
}

function dispositionLabel(d: string): string {
  return DISPOSITION_LABELS[d] ?? d.replace(/_/g, ' ')
}

/**
 * AMTC emergency-lead queue (M15-T06). The call-ahead flow (ADR-0035) writes a
 * lead for every emergency; the concierge calls back and marks it. amtc_admin
 * only (patient PII). Kept a separate page from Leads — different fields and, an
 * important distinction, these leads are UNVERIFIED (no OTP): the caller number
 * has not been phone-checked, so treat it accordingly.
 */
export function EmergencyRequestsPage(): ReactNode {
  const { token } = useAuth()
  const [status, setStatus] = useState<string | null>(null)

  // This page renders CARDS, not a DataTable, so it never registered with the
  // filter context and the header box sat disabled on it (found in M24-T03).
  // Registering for server search is what turns the box on here — no second
  // control, and the box now reaches the whole queue rather than the cards on
  // screen.
  const { term, setResultCount } = useServerSearch(SEARCHABLE.emergency)
  const query = useQuery({
    queryKey: [...EMERGENCY_LEADS_KEY, status, term],
    queryFn: () => fetchEmergencyLeads(token, status, term),
    enabled: Boolean(token),
  })

  const leads = query.data?.data ?? []
  useEffect(() => {
    setResultCount(query.data === undefined ? null : leads.length)
  }, [query.data, leads.length, setResultCount])

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-ink-1">
          Emergency leads (old flow)
        </h1>
        {/* M36-T11: READ-ONLY. The old call-ahead flow was retired at the
            switch-over; its records are kept exactly as they were. */}
        <p
          role="note"
          className="mt-2 rounded-[10px] border border-line-2 bg-bg-soft px-3 py-2 text-sm text-ink-1"
        >
          Read-only. These records come from the old emergency flow, retired
          when Emergency Help replaced it. Work live requests in{' '}
          <Link
            to={COORDINATION_PATH}
            className="font-semibold text-accent-ink underline"
          >
            Emergency coordination
          </Link>
          .
        </p>
        <p className="mt-2 text-sm text-ink-3">
          {/* M17-T14: text-bed-bad carries the "be careful" weight at 5.9:1. */}
          <span className="font-medium text-bed-bad">
            Numbers are unverified — the old flow had no phone check.
          </span>
        </p>
      </div>

      {/* The frame repeats the Dashboard's four stat tiles above this queue
          (M23 gap A7). They are whole-portal counts; above an emergency list
          they describe something other than the list beneath them, which is
          worse than absent. Not ported. */}
      <FilterTabs tabs={STATUS_FILTERS} active={status} onChange={setStatus} />

      {query.isPending ? (
        <div
          role="status"
          aria-label="Loading emergency leads"
          className="h-40 w-full max-w-3xl animate-pulse rounded-2xl bg-bg-sunken"
        />
      ) : query.isError ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-4 rounded-[10px] border border-bed-bad/40 bg-bed-bad-bg px-4 py-3"
        >
          <p className="text-sm font-medium text-bed-bad">
            {query.error instanceof ApiError
              ? query.error.message
              : 'Could not load leads.'}
          </p>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="inline-flex h-8 items-center justify-center rounded-[8px] border border-line-2 bg-bg-app px-3 text-sm font-medium text-ink-1 hover:bg-bg-soft"
          >
            Retry
          </button>
        </div>
      ) : leads.length === 0 ? (
        <div className="rounded-2xl border border-line-1">
          {/* An empty emergency queue is good news and should read that way. */}
          <EmptyState title="No emergency leads.">
            Nobody is waiting on a call back right now.
          </EmptyState>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* This queue is capped at the newest 50 and, before M24-T03, said so
              nowhere — so a concierge scrolling to the bottom had no way to know
              whether they had reached the end of the queue or the end of the
              page. */}
          <ListDisclosure shown={leads.length} total={query.data?.total} />
          {leads.map((lead) => (
            <EmergencyRow key={lead.reference} lead={lead} />
          ))}
        </div>
      )}
    </section>
  )
}

/** One legacy record, read-only (M36-T11): no status control, no notes box. */
function EmergencyRow({ lead }: { lead: AdminEmergencyLead }): ReactNode {
  const conscious =
    lead.patient_conscious && lead.patient_conscious !== 'yes'
      ? ` · ${lead.patient_conscious === 'no' ? 'unconscious' : 'intermittent'}`
      : ''

  const phone = lead.caller_phone

  // The frame draws the same card twice with two vocabularies — CALLER/WHERE/
  // GOING TO/TRANSPORT against CALLER DETAILS/LOCATION/DESTINATION/AMBULANCE
  // TYPE (M23 defect D14). The shorter set ships; it matches the register the
  // rest of the admin uses.
  //
  // TRANSPORT is NOT among them (gap A1): AdminEmergencyLead has hospital_name
  // and disposition, and neither is a vehicle.
  const facts: Fact[] = [
    {
      label: 'CALLER',
      value: (
        <>
          {lead.caller_name ?? '—'}
          {phone ? (
            <>
              {' · '}
              <a
                href={`tel:${phone}`}
                className="font-medium text-accent-ink hover:underline"
              >
                {phone}
              </a>
              {!lead.phone_verified && (
                // Attached to the number they are about to dial, not only to
                // the banner at the top of the page.
                <span className="ml-1.5 rounded-full bg-bed-warn-bg px-1.5 py-0.5 text-[11px] font-medium text-ink-1">
                  unverified
                </span>
              )}
            </>
          ) : null}
        </>
      ),
    },
    { label: 'WHERE', value: lead.location_text ?? 'Not given' },
    {
      label: 'GOING TO',
      // Null until the patient reaches the what-next step — never an empty cell.
      value: lead.hospital_name ?? 'Not yet chosen',
    },
    {
      label: 'PATIENT',
      value: (
        <>
          {lead.patient_name ?? '—'}
          {(lead.patient_age != null || conscious) && (
            <span className="text-ink-3">
              {' '}
              {lead.patient_age != null ? `${lead.patient_age}y` : ''}
              {conscious}
            </span>
          )}
        </>
      ),
    },
  ]

  return (
    <RecordCard
      title={typeLabel(lead.emergency_type)}
      reference={lead.reference}
      age={compactAge(lead.created_at)}
      status={lead.status}
      quote={lead.description}
      facts={facts}
      actions={
        <>
          {lead.purged_at ? (
            // M37-T02: the 90-day deletion has run on this record.
            <span className="text-sm text-ink-3">
              Details deleted after 90 days
            </span>
          ) : phone ? (
            <a
              href={`tel:${phone}`}
              className="inline-flex h-9 items-center justify-center rounded-[8px] bg-accent px-3.5 text-sm font-semibold text-accent-on hover:opacity-90"
            >
              Call {phone}
            </a>
          ) : (
            <span className="inline-flex h-9 cursor-not-allowed items-center rounded-[8px] border border-line-2 px-3.5 text-sm text-ink-3">
              No number given
            </span>
          )}

          {lead.concierge_notes ? (
            <p className="w-full text-sm text-ink-2">
              <span className="text-ink-3">Notes: </span>
              {lead.concierge_notes}
            </p>
          ) : null}
        </>
      }
    >
      {lead.disposition ? (
        <p className="mt-4 text-sm font-medium text-accent-ink">
          → {dispositionLabel(lead.disposition)}
          {lead.hospital_name ? ` · ${lead.hospital_name}` : ''}
        </p>
      ) : null}
    </RecordCard>
  )
}
